import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getDocument,
  updateDocument,
} from "../config";
import moment from "moment";
import { getAlgoliaClient } from "../../services/algolia";
import { setAlgoliaKey } from "./algolia";
import { refresh as refreshDashboard } from "./dashboard";
import { bulkUpdate } from "./event_reservations";
import {
  changeDate,
  changeEventType,
  changeLocation,
  changePayout,
  changeStatus,
  savePayment,
  saveResults,
} from "./reservation";
import {
  buildReservationWidgetRequests,
  parseReservationWidgetResults,
} from "./reservationWidgetQueries.mjs";
import { resolveLocationWidgetRows } from "../../services/locationRankings.mjs";

const WIDGET_IDS = ["w0", "w1", "w2", "w3", "w4", "w5"];
export const MAX_WIDGETS = 18;

/**
 * Widgets served live from Algolia rather than from the cached user_aggregates doc.
 * @see reservationWidgetQueries.mjs
 */
export const RESERVATION_WIDGET_TYPES = new Set([
  "actionTiles",
  "upcomingEvents",
  "pastEvents",
]);

/**
 * Algolia indexing is eventually consistent, so a re-query fired the instant a
 * mutation resolves can still read the pre-mutation document. Wait this long once —
 * a single bounded delay, not a retry loop.
 */
export const RESERVATION_REINDEX_DELAY_MS = 1500;

/** Reservation mutations that can change a tile count or a widget row. */
const RESERVATION_MUTATIONS = [
  bulkUpdate,
  changeStatus,
  saveResults,
  savePayment,
  changeEventType,
  changeDate,
  changeLocation,
  changePayout,
];

// In-flight batch shared by all three widgets so a single dashboard render issues one
// network request rather than three. Keyed by generation: a bump (mutation, manual
// refresh) invalidates the share and forces a fresh query.
let reservationBatch = null;
let reservationBatchGen = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run — or join — the single secured Algolia `multipleQueries` batch backing the
 * Action Items, Upcoming Events and Past Events widgets.
 * @returns {Promise<{actionTiles: object, upcomingEvents: object[], pastEvents: object[]}>}
 */
const runReservationBatch = (generation, delayMs, { getState, dispatch }) => {
  if (reservationBatch && reservationBatchGen === generation) return reservationBatch;

  reservationBatchGen = generation;
  reservationBatch = (async () => {
    if (delayMs) await sleep(delayMs);

    let key = getState().algolia.key;
    if (!key) key = await dispatch(setAlgoliaKey()).unwrap();
    if (!key) throw new Error("Missing Algolia search key");

    const { requests, countTileKeys } = buildReservationWidgetRequests({
      projectId: process.env.GATSBY_FIREBASE_CONFIG_PROJECT_ID,
      nowUnix: moment().unix(),
    });

    const { results } = await getAlgoliaClient(key).multipleQueries(requests);
    return parseReservationWidgetResults(results, countTileKeys);
  })();

  // Release the shared handle once settled so returning to the dashboard re-queries.
  reservationBatch
    .catch(() => {})
    .then(() => {
      if (reservationBatchGen === generation) reservationBatch = null;
    });

  return reservationBatch;
};

const makeMap = (val) =>
  WIDGET_IDS.reduce((acc, id) => ({ ...acc, [id]: val }), {});

// ---------- helpers ----------

/** Convert a quickDates label string to a { start, end } moment range. */
const getDateRangeFromLabel = (label) => {
  const now = moment();
  switch (label) {
    case "This Week":
      return { start: now.clone().startOf("week"), end: now.clone().endOf("week") };
    case "This Month":
      return { start: now.clone().startOf("month"), end: now.clone().endOf("month") };
    case "This Quarter":
      return { start: now.clone().startOf("quarter"), end: now.clone().endOf("quarter") };
    case "This Year":
      return { start: now.clone().startOf("year"), end: now.clone().endOf("year") };
    case "Last Week":
      return { start: now.clone().subtract(1, "week").startOf("week"), end: now.clone().subtract(1, "week").endOf("week") };
    case "Last Month":
      return { start: now.clone().subtract(1, "month").startOf("month"), end: now.clone().subtract(1, "month").endOf("month") };
    case "Last Quarter":
      return { start: now.clone().subtract(1, "quarter").startOf("quarter"), end: now.clone().subtract(1, "quarter").endOf("quarter") };
    case "Last Year":
      return { start: now.clone().subtract(1, "year").startOf("year"), end: now.clone().subtract(1, "year").endOf("year") };
    case "Trailing 12 Months":
      return { start: now.clone().subtract(11, "months").startOf("month"), end: now.clone().endOf("month") };
    case "Since Inception":
    default:
      return { start: moment("2010-01-01"), end: now.clone().endOf("day") };
  }
};

// ---------- thunks ----------

/** Load the persisted widget config from users/{uid}.widgetConfig */
export const loadWidgetConfig = createAsyncThunk(
  "widgets/loadWidgetConfig",
  async (_, { getState, rejectWithValue }) => {
    const { account } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      const userDoc = await getDocument("users", uid);
      return {
        widgetConfig: userDoc?.widgetConfig || {},
        widgetIds: userDoc?.widgetIds || null,
      };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

/** Persist a single widget slot's config to users/{uid}.widgetConfig */
export const saveWidgetConfig = createAsyncThunk(
  "widgets/saveWidgetConfig",
  async ({ widgetId, dataType, timeRange }, { getState, rejectWithValue }) => {
    const { account, widgets } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      const newConfig = {
        ...widgets.config,
        [widgetId]: { dataType: dataType || null, timeRange: timeRange || null },
      };
      await updateDocument({ entity: "users", id: uid }, { widgetConfig: newConfig });
      return { widgetId, dataType: dataType || null, timeRange: timeRange || null };
    } catch (e) {
      return rejectWithValue({ widgetId, error: e.message });
    }
  }
);

/** Persist the current widgetIds list to users/{uid}.widgetIds */
export const saveWidgetIds = createAsyncThunk(
  "widgets/saveWidgetIds",
  async (_, { getState, rejectWithValue }) => {
    const { account, widgets } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      await updateDocument({ entity: "users", id: uid }, { widgetIds: widgets.widgetIds });
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

/** Remove a widget slot and persist widgetIds + widgetConfig */
export const removeWidgetAndPersist = createAsyncThunk(
  "widgets/removeWidgetAndPersist",
  async (widgetId, { getState, rejectWithValue }) => {
    const { account, widgets } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      const nextIds = widgets.widgetIds.filter((id) => id !== widgetId);
      const nextConfig = { ...widgets.config };
      delete nextConfig[widgetId];
      await updateDocument(
        { entity: "users", id: uid },
        { widgetIds: nextIds, widgetConfig: nextConfig }
      );
      return { widgetId };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

/** Time ranges that use daily granularity in bar charts */
const DAILY_CHART_RANGES = new Set(["This Week", "Last Week", "This Month", "Last Month"]);

/** Filter monthlyData to entries within the given moment range */
const filterMonthlyData = (monthlyData, range) => {
  if (!monthlyData || !monthlyData.length) return [];
  return monthlyData.filter((m) => {
    const monthStart = moment(m.month, "YYYY-MM").startOf("month");
    return monthStart.isSameOrAfter(range.start, "month") && monthStart.isSameOrBefore(range.end, "month");
  });
};

/** Filter dailyData to entries within the given moment range */
const filterDailyData = (dailyData, range) => {
  if (!dailyData || !dailyData.length) return [];
  return dailyData.filter((d) => {
    const day = moment(d.day, "YYYY-MM-DD");
    return day.isSameOrAfter(range.start, "day") && day.isSameOrBefore(range.end, "day");
  });
};

/** Fetch data for the widget identified by widgetId */
export const fetchWidgetData = createAsyncThunk(
  "widgets/fetchWidgetData",
  async (widgetId, { getState, dispatch, rejectWithValue }) => {
    const { account, widgets, dashboard } = getState();
    const cfg = widgets.config[widgetId];
    if (!cfg || !cfg.dataType) return { widgetId, data: null };

    const { dataType, timeRange } = cfg;
    const user = account.user;

    const generation = widgets.reservationGeneration;
    const reservationBatchFor = () =>
      runReservationBatch(
        generation,
        Math.max(0, (widgets.reservationQueryableAt || 0) - Date.now()),
        { getState, dispatch }
      );

    try {
      let data;

      switch (dataType) {
        case "summaryStats": {
          if (!timeRange || timeRange === "Since Inception") {
            data = {
              periodSales: dashboard.sales || 0,
              periodAvgSales: dashboard.avgSales || 0,
              periodDonations: dashboard.donations || 0,
              periodAvgDonation: dashboard.avgDonation || 0,
            };
          } else {
            const range = getDateRangeFromLabel(timeRange);
            const filtered = filterMonthlyData(dashboard.monthlyData, range);
            const totalSales = filtered.reduce((s, m) => s + (m.sales || 0), 0);
            const totalDonations = filtered.reduce((s, m) => s + (m.donations || 0), 0);
            const totalCompleted = filtered.reduce((s, m) => s + (m.completed || 0), 0);
            const totalEvents = filtered.reduce((s, m) => s + (m.events || 0), 0);
            data = {
              periodSales: totalSales,
              periodAvgSales: totalEvents > 0 ? totalSales / totalEvents : 0,
              periodDonations: totalDonations,
              periodAvgDonation: totalCompleted > 0 ? totalDonations / totalCompleted : 0,
            };
          }
          break;
        }

        case "actionTiles": {
          const batch = await reservationBatchFor();
          data = {
            ...batch.actionTiles,
            showMissingInfo: !!(user?.permissions?.admin || user?.missingInfoStatus),
            showConfirmResults: !!(user?.permissions?.admin || (user?.extraFields && user?.extraFields.payment)),
          };
          break;
        }

        case "upcomingEvents": {
          data = (await reservationBatchFor()).upcomingEvents;
          break;
        }

        case "pastEvents": {
          data = (await reservationBatchFor()).pastEvents;
          break;
        }

        case "topFundraisers": {
          data = (dashboard.topFundraisers || []).slice(0, 10);
          break;
        }

        // Top and Bottom Locations are ranked on the server across the account's
        // full eligible location set (issue #422). They previously re-ranked here
        // inside `locationStats` — a list capped at 20 rows purely to keep the
        // aggregates doc under Firestore's 1 MiB limit — which made a payload
        // guard double as the ranking pool. resolveLocationWidgetRows() falls back
        // to that legacy path only for aggregate docs written before the fix.
        case "topLocations":
        case "bottomLocations": {
          data = resolveLocationWidgetRows(dashboard, dataType, timeRange, {
            getDateRange: getDateRangeFromLabel,
            filterMonthly: filterMonthlyData,
          });
          break;
        }

        case "aggregateStatsSales": {
          const monthly = dashboard.monthlyData || [];
          const effectiveRange = (timeRange && timeRange !== "Since Inception") ? timeRange : "Trailing 12 Months";
          const range = getDateRangeFromLabel(effectiveRange);
          let labels, salesData;

          if (DAILY_CHART_RANGES.has(effectiveRange)) {
            const daily = dashboard.dailyData || [];
            const dailyFiltered = filterDailyData(daily, range);
            if (dailyFiltered.length > 0) {
              const labelFormat = (effectiveRange === "This Week" || effectiveRange === "Last Week") ? "ddd M/D" : "MMM D";
              labels = dailyFiltered.map((d) => moment(d.day, "YYYY-MM-DD").format(labelFormat));
              salesData = dailyFiltered.map((d) => d.sales || 0);
            } else {
              // dailyData not yet populated — fall back to monthly
              const rangeFiltered = filterMonthlyData(monthly, range);
              const rangeHasData = rangeFiltered.some((m) => (m.sales || 0) > 0);
              const fallback = rangeHasData ? rangeFiltered : monthly.slice(-12);
              labels = fallback.map((m) => moment(m.month, "YYYY-MM").format("MMM YYYY"));
              salesData = fallback.map((m) => m.sales || 0);
            }
          } else {
            const rangeFiltered = filterMonthlyData(monthly, range);
            const filtered = rangeFiltered.length > 0 ? rangeFiltered : monthly.slice(-12);
            labels = filtered.map((m) => moment(m.month, "YYYY-MM").format("MMM YYYY"));
            salesData = filtered.map((m) => m.sales || 0);
          }

          data = {
            labels,
            datasets: [
              {
                label: "Total Sales",
                backgroundColor: "#00d78b",
                borderColor: "#00d78b",
                data: salesData,
              },
            ],
          };
          break;
        }

        case "aggregateStatsEvents": {
          const monthly = dashboard.monthlyData || [];
          const effectiveRange = (timeRange && timeRange !== "Since Inception") ? timeRange : "Trailing 12 Months";
          const range = getDateRangeFromLabel(effectiveRange);
          let labels, eventRequestsData, eventsData;

          if (DAILY_CHART_RANGES.has(effectiveRange)) {
            const daily = dashboard.dailyData || [];
            const dailyFiltered = filterDailyData(daily, range);
            const dailyHasData = dailyFiltered.some((d) => (d.eventRequests || 0) > 0 || (d.events || 0) > 0);
            if (dailyHasData) {
              const labelFormat = (effectiveRange === "This Week" || effectiveRange === "Last Week") ? "ddd M/D" : "MMM D";
              labels = dailyFiltered.map((d) => moment(d.day, "YYYY-MM-DD").format(labelFormat));
              eventRequestsData = dailyFiltered.map((d) => d.eventRequests || 0);
              eventsData = dailyFiltered.map((d) => d.events || 0);
            } else {
              // dailyData not yet populated — fall back to monthly
              const rangeFiltered = filterMonthlyData(monthly, range);
              const rangeHasData = rangeFiltered.some((m) => (m.eventRequests || 0) > 0 || (m.events || 0) > 0);
              const fallback = rangeHasData ? rangeFiltered : monthly.slice(-12);
              labels = fallback.map((m) => moment(m.month, "YYYY-MM").format("MMM YYYY"));
              eventRequestsData = fallback.map((m) => m.eventRequests || 0);
              eventsData = fallback.map((m) => m.events || 0);
            }
          } else {
            const rangeFiltered = filterMonthlyData(monthly, range);
            const rangeHasData = rangeFiltered.some((m) => (m.eventRequests || 0) > 0 || (m.events || 0) > 0);
            const filtered = rangeHasData ? rangeFiltered : monthly.slice(-12);
            labels = filtered.map((m) => moment(m.month, "YYYY-MM").format("MMM YYYY"));
            eventRequestsData = filtered.map((m) => m.eventRequests || 0);
            eventsData = filtered.map((m) => m.events || 0);
          }

          data = {
            labels,
            datasets: [
              {
                label: "Event Requests",
                backgroundColor: "#00a2ca",
                borderColor: "#00a2ca",
                data: eventRequestsData,
              },
              {
                label: "Events",
                backgroundColor: "#00d78b",
                borderColor: "#00d78b",
                data: eventsData,
              },
            ],
          };
          break;
        }

        case "eventCalendar": {
          const calStart = moment().startOf("month");
          const months = [];
          for (let i = 0; i < 12; i++) {
            const m = calStart.clone().add(i, "months");
            months.push({
              year: m.year(),
              month: m.month(),
              label: m.format("MMMM YYYY"),
            });
          }
          data = { months, byDate: dashboard.calendarByDate || {} };
          break;
        }

        case "orgTypeSummary": {
          if (!timeRange || timeRange === "Since Inception") {
            data = (dashboard.orgTypeSummary || []).slice(0, 10);
          } else {
            const range = getDateRangeFromLabel(timeRange);
            const filtered = filterMonthlyData(dashboard.orgTypeMonthlyData || [], range);
            const totals = {};
            filtered.forEach((entry) => {
              if (!totals[entry.orgType]) totals[entry.orgType] = { org_type: entry.orgType, events: 0, total_sales: 0 };
              totals[entry.orgType].events += entry.events || 0;
              totals[entry.orgType].total_sales += entry.totalSales || 0;
            });
            const totalEvents = Object.values(totals).reduce((s, t) => s + t.events, 0);
            data = Object.values(totals)
              .map((t) => ({
                ...t,
                pct_of_total: totalEvents > 0 ? parseFloat(((t.events / totalEvents) * 100).toFixed(1)) : 0,
                avg_sales: t.events > 0 ? t.total_sales / t.events : 0,
              }))
              .sort((a, b) => b.events - a.events);
          }
          break;
        }

        case "compStats": {
          const monthly = dashboard.monthlyData || [];
          let granular;
          if (!timeRange || timeRange === "Since Inception" || timeRange === "Trailing 12 Months") {
            const current = monthly.slice(-12);
            const previous = monthly.slice(0, 12);
            granular = current.map((m, i) => ({
              chart_date: moment(m.month, "YYYY-MM").format("MMM YYYY"),
              current_total_sales: m.sales || 0,
              previous_total_sales: previous[i]?.sales || 0,
            }));
          } else {
            const range = getDateRangeFromLabel(timeRange);
            const filtered = filterMonthlyData(monthly, range);
            const base = filtered.some((m) => (m.sales || 0) > 0) ? filtered : monthly.slice(-12);
            const priorRange = {
              start: range.start.clone().subtract(1, "year"),
              end: range.end.clone().subtract(1, "year"),
            };
            const priorFiltered = filterMonthlyData(monthly, priorRange);
            granular = base.map((m, i) => ({
              chart_date: moment(m.month, "YYYY-MM").format("MMM YYYY"),
              current_total_sales: m.sales || 0,
              previous_total_sales: priorFiltered[i]?.sales || 0,
            }));
          }
          data = { granular };
          break;
        }

        default:
          data = null;
      }

      return { widgetId, data };
    } catch (e) {
      return rejectWithValue({ widgetId, error: e.message });
    }
  }
);

// ---------- slice ----------

export const widgetsSlice = createSlice({
  name: "widgets",
  initialState: {
    widgetIds: [...WIDGET_IDS],
    config: makeMap({ dataType: null, timeRange: null }),
    configLoading: false,
    configError: null,
    data: makeMap(null),
    loading: makeMap(false),
    errors: makeMap(null),
    // Bumped whenever the live Algolia-backed reservation widgets must re-query.
    reservationGeneration: 0,
    // Epoch ms before which a re-query would risk reading a pre-mutation index.
    reservationQueryableAt: 0,
  },
  reducers: {
    setWidgetConfig: (state, action) => {
      const { widgetId, dataType, timeRange } = action.payload;
      state.config[widgetId] = { dataType: dataType || null, timeRange: timeRange || null };
    },
    clearWidgetData: (state, action) => {
      const widgetId = action.payload;
      state.data[widgetId] = null;
      state.errors[widgetId] = null;
    },
    reorderWidgets: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      const [removed] = state.widgetIds.splice(oldIndex, 1);
      state.widgetIds.splice(newIndex, 0, removed);
    },
    addWidgetSlot: (state) => {
      if (state.widgetIds.length >= MAX_WIDGETS) return;
      const maxNum = state.widgetIds.reduce((max, id) => {
        const m = /^w(\d+)$/.exec(String(id));
        return m ? Math.max(max, parseInt(m[1], 10)) : max;
      }, -1);
      const nextId = `w${maxNum + 1}`;
      state.widgetIds.push(nextId);
      state.config[nextId] = { dataType: null, timeRange: null };
      state.data[nextId] = null;
      state.loading[nextId] = false;
      state.errors[nextId] = null;
    },
  },
  extraReducers: (builder) => {
    // loadWidgetConfig
    builder
      .addCase(loadWidgetConfig.pending, (state) => {
        state.configLoading = true;
        state.configError = null;
      })
      .addCase(loadWidgetConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        const { widgetConfig, widgetIds } = action.payload;
        // Restore persisted widget ID list (or fall back to default)
        const ids = (widgetIds && widgetIds.length >= WIDGET_IDS.length)
          ? widgetIds
          : WIDGET_IDS;
        state.widgetIds = ids;
        ids.forEach((id) => {
          state.config[id] = widgetConfig[id] || { dataType: null, timeRange: null };
          if (state.data[id] === undefined) state.data[id] = null;
          if (state.loading[id] === undefined) state.loading[id] = false;
          if (state.errors[id] === undefined) state.errors[id] = null;
        });
      })
      .addCase(loadWidgetConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.configError = action.payload;
      });

    builder.addCase(removeWidgetAndPersist.fulfilled, (state, action) => {
      const { widgetId } = action.payload;
      state.widgetIds = state.widgetIds.filter((id) => id !== widgetId);
      delete state.config[widgetId];
      delete state.data[widgetId];
      delete state.loading[widgetId];
      delete state.errors[widgetId];
    });

    // saveWidgetConfig
    builder
      .addCase(saveWidgetConfig.fulfilled, (state, action) => {
        const { widgetId, dataType, timeRange } = action.payload;
        state.config[widgetId] = { dataType, timeRange };
      })
      .addCase(saveWidgetConfig.rejected, (state, action) => {
        if (action.payload?.widgetId) {
          state.errors[action.payload.widgetId] = action.payload.error;
        }
      });

    // fetchWidgetData
    builder
      .addCase(fetchWidgetData.pending, (state, action) => {
        const widgetId = action.meta.arg;
        state.loading[widgetId] = true;
        state.errors[widgetId] = null;
      })
      .addCase(fetchWidgetData.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { widgetId, data } = action.payload;
        state.loading[widgetId] = false;
        state.data[widgetId] = data;
      })
      .addCase(fetchWidgetData.rejected, (state, action) => {
        const payload = action.payload || {};
        const widgetId = payload.widgetId || action.meta.arg;
        state.loading[widgetId] = false;
        state.errors[widgetId] = payload.error || "Failed to load widget data.";
      });

    // Live reservation widgets: re-query after any mutation that can move a count or a
    // row, and when the user hits the dashboard Refresh button. Mutations also push out
    // `reservationQueryableAt` so a widget mounted at the time waits for the reindex —
    // but a user who mutates on /events and wanders back later waits for nothing.
    RESERVATION_MUTATIONS.forEach((thunk) => {
      builder.addCase(thunk.fulfilled, (state) => {
        state.reservationGeneration += 1;
        state.reservationQueryableAt = Date.now() + RESERVATION_REINDEX_DELAY_MS;
      });
    });
    builder.addCase(refreshDashboard.pending, (state) => {
      state.reservationGeneration += 1;
    });
  },
});

export const { setWidgetConfig, clearWidgetData, addWidgetSlot, reorderWidgets } = widgetsSlice.actions;
export default widgetsSlice.reducer;
